// src/components/AdminDashboard.js
import React, { useState, useEffect } from 'react';
import { Calendar, DollarSign, Package, ShoppingBag, ChevronRight, Search } from 'lucide-react';
import DeliveryCalendar from './DeliveryCalendar';
import OrdersView from './OrdersView';
import ProductsManagement from './ProductsManagement';

const AdminDashboard = () => {
  const [view, setView] = useState('overview');
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({
    totalOrders: 0,
    revenue: 0,
    pendingDeliveries: 0,
    thisWeekDeliveries: 0
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      console.log('Making request to:', `${process.env.REACT_APP_BACKEND_URL}/api/admin/orders`);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/orders`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          // Include Authorization header for backward compatibility
          ...(localStorage.getItem('token') && {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          })
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Fetched orders:', data);
      
      setOrders(data.orders || []);
      setStats(data.stats || {
        totalOrders: 0,
        revenue: 0,
        pendingDeliveries: 0,
        thisWeekDeliveries: 0
      });
    } catch (error) {
      console.error('Error fetching orders:', error);
      setOrders([]);
      setStats({
        totalOrders: 0,
        revenue: 0,
        pendingDeliveries: 0,
        thisWeekDeliveries: 0
      });
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      console.log('Updating order:', orderId, 'to status:', newStatus);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/orders/${orderId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus }),
      });

      console.log('Update response status:', response.status);
      const data = await response.json();
      console.log('Update response:', data);

      if (response.ok) {
        fetchOrders(); // Refresh orders after successful update
      } else {
        console.error('Failed to update status:', data.error);
      }
    } catch (error) {
      console.error('Error updating order:', error);
    }
  };

  const filteredOrders = orders?.filter(order => {
    if (!order) return false;
    const matchesSearch = (order.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          order._id?.toString().includes(searchTerm));
    const matchesFilter = filterStatus === 'all' || order.status === filterStatus;
    return matchesSearch && matchesFilter;
  }) || [];

  return (
    <div className="min-h-screen bg-green-100">
      {/* Navigation */}
      <nav className="bg-amber-50 shadow-md p-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="text-2xl font-bold text-rose-800">Nena Miami Admin</div>
          <div className="space-x-4">
            <button 
              onClick={() => setView('overview')}
              className={`px-4 py-2 rounded-md ${view === 'overview' ? 'bg-rose-800 text-white' : 'text-rose-800'}`}
            >
              Overview
            </button>
            <button 
              onClick={() => setView('orders')}
              className={`px-4 py-2 rounded-md ${view === 'orders' ? 'bg-rose-800 text-white' : 'text-rose-800'}`}
            >
              Orders
            </button>
            <button 
              onClick={() => setView('products')}
              className={`px-4 py-2 rounded-md ${view === 'products' ? 'bg-rose-800 text-white' : 'text-rose-800'}`}
            >
              Products
            </button>
            <button 
              onClick={() => setView('calendar')}
              className={`px-4 py-2 rounded-md ${view === 'calendar' ? 'bg-rose-800 text-white' : 'text-rose-800'}`}
            >
              Calendar
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto p-6">
        {/* Stats Overview - Always visible */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-amber-50 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600">Total Orders</p>
                <h3 className="text-2xl font-bold text-rose-800">{stats.totalOrders}</h3>
              </div>
              <Package className="w-8 h-8 text-rose-800" />
            </div>
          </div>

          <div className="bg-amber-50 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600">Total Revenue</p>
                <h3 className="text-2xl font-bold text-rose-800">${stats.revenue}</h3>
              </div>
              <DollarSign className="w-8 h-8 text-rose-800" />
            </div>
          </div>

          <div className="bg-amber-50 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600">Pending Orders</p>
                <h3 className="text-2xl font-bold text-rose-800">{stats.pendingDeliveries}</h3>
              </div>
              <ShoppingBag className="w-8 h-8 text-rose-800" />
            </div>
          </div>

          <div className="bg-amber-50 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600">This Week's Deliveries</p>
                <h3 className="text-2xl font-bold text-rose-800">{stats.thisWeekDeliveries}</h3>
              </div>
              <Calendar className="w-8 h-8 text-rose-800" />
            </div>
          </div>
        </div>

        {/* View Content */}
        {view === 'overview' && (
          <div className="bg-amber-50 rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-rose-800">Recent Orders</h2>
              <div className="flex gap-4">
                <div className="relative">
                  <Search className="w-5 h-5 absolute left-3 top-2.5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search orders..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-rose-500"
                  />
                </div>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-rose-500"
                >
                  <option value="all">All Status</option>
                  <option value="Pending">Pending</option>
                  <option value="Confirmed">Confirmed</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Completed">Completed</option>
                </select>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left border-b border-gray-200">
                    <th className="pb-3 pl-4">Order ID</th>
                    <th className="pb-3">Customer</th>
                    <th className="pb-3">Delivery Date</th>
                    <th className="pb-3">Amount</th>
                    <th className="pb-3">Status</th>
                    <th className="pb-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map((order) => (
                    <tr key={order._id} className="border-b border-gray-200 hover:bg-amber-100">
                      <td className="py-4 pl-4">#{order._id}</td>
                      <td className="py-4">{order.customerName}</td>
                      <td className="py-4">{new Date(order.deliveryDate).toLocaleDateString()}</td>
                      <td className="py-4">${order.totalAmount}</td>
                      <td className="py-4">
                        <select
                          value={order.status}
                          onChange={(e) => updateOrderStatus(order._id, e.target.value)}
                          className={`px-3 py-1 rounded-full text-sm ${
                            order.status === 'Completed' ? 'bg-green-100 text-green-800' :
                            order.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
                            order.status === 'In Progress' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}
                        >
                          <option value="Pending">Pending</option>
                          <option value="Confirmed">Confirmed</option>
                          <option value="In Progress">In Progress</option>
                          <option value="Completed">Completed</option>
                        </select>
                      </td>
                      <td className="py-4">
                        <button 
                          onClick={() => {/* View order details */}}
                          className="p-2 hover:bg-rose-100 rounded-full"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {view === 'orders' && (
          <OrdersView orders={orders} updateOrderStatus={updateOrderStatus} />
        )}

        {view === 'products' && (
          <ProductsManagement />
        )}

        {view === 'calendar' && (
          <div className="bg-amber-50 rounded-lg shadow p-6">
            <h2 className="text-xl font-bold text-rose-800 mb-4">Delivery Calendar</h2>
            <DeliveryCalendar orders={orders} />
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;